import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, InputNumber, Typography, Divider, Alert, Space, Checkbox, Select, Button, Spin, message } from 'antd';
import { MailOutlined, ApiOutlined, SearchOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Paragraph, Text } = Typography;

// 圖檔圖標組件（支持圖片的標記）
const ImageFileIcon = ({ style = {} }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 640 640" 
    style={{ 
      width: '14px', 
      height: '14px', 
      display: 'inline-block',
      verticalAlign: 'middle',
      marginLeft: '4px',
      ...style 
    }}
  >
    <path 
      d="M160 96C124.7 96 96 124.7 96 160L96 480C96 515.3 124.7 544 160 544L480 544C515.3 544 544 515.3 544 480L544 160C544 124.7 515.3 96 480 96L160 96zM224 176C250.5 176 272 197.5 272 224C272 250.5 250.5 272 224 272C197.5 272 176 250.5 176 224C176 197.5 197.5 176 224 176zM368 288C376.4 288 384.1 292.4 388.5 299.5L476.5 443.5C481 450.9 481.2 460.2 477 467.8C472.8 475.4 464.7 480 456 480L184 480C175.1 480 166.8 475 162.7 467.1C158.6 459.2 159.2 449.6 164.3 442.3L220.3 362.3C224.8 355.9 232.1 352.1 240 352.1C247.9 352.1 255.2 355.9 259.7 362.3L286.1 400.1L347.5 299.6C351.9 292.5 359.6 288.1 368 288.1z" 
      fill="currentColor"
    />
  </svg>
);

const mapLabeledModelsToSelectOptions = (models) => models.map((model) => ({
  value: model.value,
  label: model.supportsVision ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      {model.label}
      <ImageFileIcon style={{ color: '#1890ff' }} />
    </span>
  ) : model.label,
  searchText: model.label
}));

const ApiProviderSettingsModal = ({
  open,
  provider,
  onCancel,
  onSubmit,
  loading,
  fetching,
  isTenantAdmin = false,
  t
}) => {
  const [form] = Form.useForm();

  const formatJson = React.useCallback((value) => {
    if (!value || typeof value !== 'string') {
      return '';
    }

    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      return value;
    }
  }, []);

  const normalizeJson = React.useCallback((value) => {
    if (!value || typeof value !== 'string' || value.trim().length === 0) {
      return null;
    }

    const trimmed = value.trim();

    try {
      const parsed = JSON.parse(trimmed);
      return JSON.stringify(parsed);
    } catch (error) {
      throw new Error('invalid');
    }
  }, []);

  const authTypeOptions = React.useMemo(() => ([
    { value: 'apiKey', label: t('apiProviders.form.authTypeOptions.apiKey') },
    { value: 'bearerToken', label: t('apiProviders.form.authTypeOptions.bearerToken') },
    { value: 'serviceAccount', label: t('apiProviders.form.authTypeOptions.serviceAccount') },
    { value: 'oauth', label: t('apiProviders.form.authTypeOptions.oauth') }
  ]), [t]);

  useEffect(() => {
    if (provider) {
      // Debug log for verifying provider payload passed into modal
      // eslint-disable-next-line no-console
      console.log('[ApiProviderSettingsModal] received provider', provider);

      const settingsJson = formatJson(provider.settingsJson || provider.defaultSettingsJson || '');
      const extraHeadersJson = formatJson(provider.extraHeadersJson);
      const authConfigJson = formatJson(provider.authConfigJson);

      // 解析 authConfigJson 為獨立欄位（用於 Email Server）
      let tenantId = '';
      let clientId = '';
      let clientSecret = '';
      if (authConfigJson) {
        try {
          const parsed = JSON.parse(authConfigJson);
          tenantId = parsed.tenantId || '';
          clientId = parsed.clientId || '';
          clientSecret = parsed.clientSecret || '';
        } catch (e) {
          // 忽略解析錯誤
        }
      }

      // 解析 settingsJson 為獨立欄位（用於 Email Server）
      let fromEmail = '';
      let replyTo = '';
      let saveToSentItems = true;
      let structuredOutputMode = 'auto';
      let structuredOutputStrict = true;
      // 解析 settingsJson 為獨立欄位（用於 ERP / Odoo）
      let odooProtocol = 'xmlrpc';
      let odooDatabase = '';
      let odooDefaultLogin = '';
      let odooPort = 443;
      let odooTimeoutSeconds = 30;
      let odooDefaultCompanyId = null;
      // 解析 settingsJson 為獨立欄位（用於 Voice）
      let voiceLanguage = 'zh';
      let voiceFormat = true;
      if (settingsJson) {
        try {
          const parsed = JSON.parse(settingsJson);
          fromEmail = parsed.fromEmail || '';
          replyTo = parsed.replyTo || '';
          saveToSentItems = parsed.saveToSentItems !== undefined ? parsed.saveToSentItems : true;
          structuredOutputMode = parsed.structuredOutputMode || 'auto';
          structuredOutputStrict = parsed.structuredOutputStrict !== undefined ? !!parsed.structuredOutputStrict : true;
          odooProtocol = parsed.protocol || 'xmlrpc';
          odooDatabase = parsed.database || '';
          odooDefaultLogin = parsed.defaultLogin || '';
          odooPort = parsed.port !== undefined ? parsed.port : 443;
          odooTimeoutSeconds = parsed.timeoutSeconds !== undefined ? parsed.timeoutSeconds : 30;
          odooDefaultCompanyId = parsed.defaultCompanyId !== undefined ? parsed.defaultCompanyId : null;
          voiceLanguage = parsed.language !== undefined ? parsed.language : 'zh';
          voiceFormat = parsed.format !== undefined ? !!parsed.format : true;
        } catch (e) {
          // 忽略解析錯誤
        }
      }

      form.setFieldsValue({
        active: provider.active,
        apiUrl: provider.apiUrl,
        model: provider.model,
        temperature: provider.temperature,
        topP: provider.topP,
        enableStreaming: provider.enableStreaming,
        extraHeadersJson,
        authType: provider.authType || provider.defaultAuthType || 'apiKey',
        authConfigJson,
        settingsJson,
        clearApiKey: false,
        apiKey: '',
        managementApiKey: '',
        clearManagementApiKey: false,
        // Email Server 專用欄位
        tenantId,
        clientId,
        clientSecret,
        fromEmail,
        replyTo,
        saveToSentItems,
        structuredOutputMode,
        structuredOutputStrict,
        // ERP / Odoo 專用欄位
        odooProtocol,
        odooDatabase,
        odooDefaultLogin,
        odooPort,
        odooTimeoutSeconds,
        odooDefaultCompanyId,
        // Voice 專用欄位
        voiceLanguage,
        voiceFormat
      });
    } else {
      form.resetFields();
    }
  }, [provider, form, formatJson]);

  const validateJsonField = React.useCallback((fieldName, value) => {
    if (!value || value.trim().length === 0) {
      form.setFields([{ name: fieldName, errors: [] }]);
      return true;
    }

    try {
      JSON.parse(value);
      form.setFields([{ name: fieldName, errors: [] }]);
      return true;
    } catch (error) {
      form.setFields([{ name: fieldName, errors: [t('apiProviders.form.jsonInvalid')] }]);
      return false;
    }
  }, [form, t]);

  const handleOk = () => {
    form.validateFields().then(values => {
      // 如果是 Email Server 類型，從獨立欄位構建 JSON
      if (isEmailServerCategory) {
        // 構建 authConfigJson
        const authConfig = {
          tenantId: values.tenantId || '',
          clientId: values.clientId || '',
          clientSecret: values.clientSecret || ''
        };
        values.authConfigJson = JSON.stringify(authConfig);

        // 構建 settingsJson
        const settings = {
          fromEmail: values.fromEmail || '',
          replyTo: values.replyTo || '',
          saveToSentItems: values.saveToSentItems !== undefined ? values.saveToSentItems : true
        };
        values.settingsJson = JSON.stringify(settings);
      }
      if (isErpCategory) {
        // 把 odoo 子欄位 merge 進 settingsJson；保留其他既有 keys
        let parsedSettings = {};
        try {
          parsedSettings = values.settingsJson ? JSON.parse(values.settingsJson) : {};
        } catch (e) {
          parsedSettings = {};
        }
        const nextSettings = {
          ...parsedSettings,
          protocol: values.odooProtocol || 'xmlrpc',
          database: values.odooDatabase || '',
          defaultLogin: values.odooDefaultLogin || '',
          port: values.odooPort ?? 443,
          timeoutSeconds: values.odooTimeoutSeconds ?? 30,
          defaultCompanyId: values.odooDefaultCompanyId ?? null
        };
        values.settingsJson = JSON.stringify(nextSettings);
      }
      if (isVoiceCategory) {
        let parsedSettings = {};
        try {
          parsedSettings = values.settingsJson ? JSON.parse(values.settingsJson) : {};
        } catch (e) {
          parsedSettings = {};
        }
        const nextSettings = {
          ...parsedSettings,
          language: values.voiceLanguage || '',
          format: values.voiceFormat !== false
        };
        values.settingsJson = JSON.stringify(nextSettings);
      }
      if (isAiCategory && !isEmailServerCategory) {
        let parsedSettings = {};
        try {
          parsedSettings = values.settingsJson ? JSON.parse(values.settingsJson) : {};
        } catch (e) {
          parsedSettings = {};
        }

        const nextSettings = {
          ...parsedSettings,
          structuredOutputMode: values.structuredOutputMode || 'auto',
          structuredOutputStrict: values.structuredOutputStrict !== false
        };
        values.settingsJson = JSON.stringify(nextSettings);
      }

      const targets = [
        ['extraHeadersJson', values.extraHeadersJson],
        ['authConfigJson', values.authConfigJson],
        ['settingsJson', values.settingsJson]
      ];

      for (const [field, content] of targets) {
        if (!validateJsonField(field, content)) {
          return;
        }
      }

      let normalizedValues;

      try {
        normalizedValues = {
          ...values,
          extraHeadersJson: normalizeJson(values.extraHeadersJson),
          authConfigJson: normalizeJson(values.authConfigJson),
          settingsJson: normalizeJson(values.settingsJson)
        };
        // 移除臨時欄位，只保留後端需要的欄位
        delete normalizedValues.tenantId;
        delete normalizedValues.clientId;
        delete normalizedValues.clientSecret;
        delete normalizedValues.fromEmail;
        delete normalizedValues.replyTo;
        delete normalizedValues.saveToSentItems;
        delete normalizedValues.structuredOutputMode;
        delete normalizedValues.structuredOutputStrict;
        delete normalizedValues.odooProtocol;
        delete normalizedValues.odooDatabase;
        delete normalizedValues.odooDefaultLogin;
        delete normalizedValues.odooPort;
        delete normalizedValues.odooTimeoutSeconds;
        delete normalizedValues.odooDefaultCompanyId;
        delete normalizedValues.voiceLanguage;
        delete normalizedValues.voiceFormat;
      } catch (error) {
        // normalizeJson throws "invalid" when JSON parsing失敗
        return;
      }

      onSubmit(normalizedValues);
    }).catch(error => {
      // 處理表單驗證錯誤
      if (error?.errorFields) {
        // Ant Design 驗證錯誤，已經在表單中顯示錯誤訊息，不需要額外處理
        // 這裡只是為了避免未捕獲的錯誤導致頁面崩潰
        return;
      }
      // 其他未預期的錯誤
      console.error('Unexpected error during form validation:', error);
    });
  };

  // xAI (Grok) 聊天模型：與 https://docs.x.ai/docs/models 一致；末尾保留舊版 model id 供既有設定相容
  const xaiAllModels = React.useMemo(() => [
    { value: 'grok-4.20-0309-reasoning', label: 'grok-4.20-0309-reasoning', supportsVision: true },
    { value: 'grok-4.20-0309-non-reasoning', label: 'grok-4.20-0309-non-reasoning', supportsVision: true },
    { value: 'grok-4-1-fast-reasoning', label: 'grok-4-1-fast-reasoning', supportsVision: true },
    { value: 'grok-4-1-fast-non-reasoning', label: 'grok-4-1-fast-non-reasoning', supportsVision: true },
    { value: 'grok-4.20-multi-agent-0309', label: 'grok-4.20-multi-agent-0309', supportsVision: true },
    { value: 'grok-4-fast-reasoning', label: 'grok-4-fast-reasoning', supportsVision: true },
    { value: 'grok-4-fast-non-reasoning', label: 'grok-4-fast-non-reasoning', supportsVision: true }
  ], []);

  // OpenAI：與 https://developers.openai.com/api/docs/models 主線／常用 Chat 模型（略過已標 Deprecated 與非聊天用途）
  const openaiAllModels = React.useMemo(() => [
    { value: 'gpt-5.4', label: 'gpt-5.4', supportsVision: true },
    { value: 'gpt-5.4-pro', label: 'gpt-5.4-pro', supportsVision: true },
    { value: 'gpt-5.4-mini', label: 'gpt-5.4-mini', supportsVision: true },
    { value: 'gpt-5.4-nano', label: 'gpt-5.4-nano', supportsVision: true },
    { value: 'gpt-5', label: 'gpt-5', supportsVision: true },
    { value: 'gpt-5-pro', label: 'gpt-5-pro', supportsVision: true },
    { value: 'gpt-5-mini', label: 'gpt-5-mini', supportsVision: true },
    { value: 'gpt-5-nano', label: 'gpt-5-nano', supportsVision: true },
    { value: 'gpt-5.2', label: 'gpt-5.2', supportsVision: true },
    { value: 'gpt-5.2-pro', label: 'gpt-5.2-pro', supportsVision: true },
    { value: 'gpt-5.1', label: 'gpt-5.1', supportsVision: true },
    { value: 'gpt-4o', label: 'gpt-4o', supportsVision: true },
    { value: 'gpt-4o-mini', label: 'gpt-4o-mini', supportsVision: true },
    { value: 'gpt-4.1', label: 'gpt-4.1', supportsVision: true },
    { value: 'gpt-4.1-mini', label: 'gpt-4.1-mini', supportsVision: true },
    { value: 'gpt-4.1-nano', label: 'gpt-4.1-nano', supportsVision: true },
    { value: 'o3', label: 'o3', supportsVision: true },
    { value: 'o3-pro', label: 'o3-pro', supportsVision: true },
    { value: 'o3-mini', label: 'o3-mini', supportsVision: true },
    { value: 'o4-mini', label: 'o4-mini', supportsVision: true }
  ], []);

  // Google Gemini：與 https://ai.google.dev/gemini-api/docs/models 2.x／2.5 主線
  const geminiAllModels = React.useMemo(() => [
    { value: 'gemini-2.5-pro', label: 'gemini-2.5-pro', supportsVision: true },
    { value: 'gemini-2.5-flash', label: 'gemini-2.5-flash', supportsVision: true },
    { value: 'gemini-2.5-flash-lite', label: 'gemini-2.5-flash-lite', supportsVision: true },
    { value: 'gemini-2.0-flash', label: 'gemini-2.0-flash', supportsVision: true }
  ], []);

  // DeepSeek：https://api-docs.deepseek.com/ （V3.2：chat + reasoner；已移除舊版 deepseek-coder）
  const deepseekAllModels = React.useMemo(() => [
    { value: 'deepseek-chat', label: 'deepseek-chat', supportsVision: false },
    { value: 'deepseek-reasoner', label: 'deepseek-reasoner', supportsVision: false }
  ], []);

  const supportedModels = React.useMemo(() => {
    const key = provider?.providerKey;
    if (key === 'xai') {
      return mapLabeledModelsToSelectOptions(xaiAllModels);
    }
    if (key === 'openai') {
      return mapLabeledModelsToSelectOptions(openaiAllModels);
    }
    if (key === 'gemini') {
      return mapLabeledModelsToSelectOptions(geminiAllModels);
    }
    if (key === 'deepseek') {
      return mapLabeledModelsToSelectOptions(deepseekAllModels);
    }

    if (!provider?.supportedModels) {
      return [];
    }

    try {
      const parsed = JSON.parse(provider.supportedModels);
      if (Array.isArray(parsed)) {
        return parsed.map(model => ({ value: model, label: model }));
      }
    } catch (error) {
      console.warn('Failed to parse supported models', error);
    }

    return [];
  }, [provider, xaiAllModels, openaiAllModels, geminiAllModels, deepseekAllModels]);

  const isAiCategory = provider?.category === 'AI';
  const isEmailServerCategory = provider?.category === 'EmailServer';
  const isErpCategory = provider?.category === 'ERP';
  const isVoiceCategory = provider?.category === 'Voice';
  const isEmbeddingCategory = provider?.category === 'Embedding';
  const isXaiCollectionsProvider = provider?.providerKey === 'xai-collections';
  const showManagementApiKey = isXaiCollectionsProvider && isTenantAdmin;
  const [testingEmail, setTestingEmail] = React.useState(false);
  const [testingEmbedding, setTestingEmbedding] = React.useState(false);
  const [listingEmbeddingModels, setListingEmbeddingModels] = React.useState(false);
  const [testEmailModalOpen, setTestEmailModalOpen] = React.useState(false);
  const [testEmailForm] = Form.useForm();
  const [testingOdoo, setTestingOdoo] = React.useState(false);

  const handleListEmbeddingModelsClick = async () => {
    if (!provider?.providerKey) return;
    setListingEmbeddingModels(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/apiproviders/list-embedding-models/${provider.providerKey}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (!response.ok) {
        message.error(result.error || 'Failed to list models');
        return;
      }
      const models = result.models || [];
      if (models.length === 0) {
        message.warning(t('apiProviders.form.embeddingModelsEmpty') || 'No embedding models found for this provider.');
        return;
      }
      Modal.info({
        title: t('apiProviders.form.embeddingModelsTitle') || 'Available Embedding Models',
        content: (
          <div>
            <p style={{ marginBottom: 8 }}>{t('apiProviders.form.embeddingModelsHint') || 'Copy a model name and paste it into the Model field above.'}</p>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {models.map(m => <li key={m}><code style={{ userSelect: 'all' }}>{m}</code></li>)}
            </ul>
          </div>
        ),
        width: 480
      });
    } catch (err) {
      message.error('Failed to list embedding models');
    } finally {
      setListingEmbeddingModels(false);
    }
  };

  const handleTestEmbeddingClick = async () => {
    if (!provider?.providerKey) return;
    setTestingEmbedding(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error(t('common.loginRequired'));
        return;
      }
      const response = await fetch(`/api/apiproviders/test-embedding/${provider.providerKey}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (!response.ok || result.ok === false) {
        const detail = result.unsupportedReason || result.error || `HTTP ${response.status}`;
        Modal.error({
          title: t('apiProviders.form.testEmbeddingFailed'),
          content: detail
        });
        return;
      }
      message.success(
        t('apiProviders.form.testEmbeddingOk', {
          model: result.model,
          dimensions: result.dimensions
        })
      );
    } catch (error) {
      console.error('Test embedding failed', error);
      message.error(t('apiProviders.form.testEmbeddingFailed'));
    } finally {
      setTestingEmbedding(false);
    }
  };

  const handleResetSettings = () => {
    if (provider?.defaultSettingsJson) {
      form.setFieldsValue({ settingsJson: formatJson(provider.defaultSettingsJson) });
    } else {
      form.setFieldsValue({ settingsJson: '' });
    }
  };

  const handleTestOdooClick = async () => {
    try {
      await form.validateFields(['apiUrl', 'odooDatabase', 'odooDefaultLogin']);
      const formValues = form.getFieldsValue();
      const apiKey = formValues.apiKey || ''; // 若使用者尚未輸入新 key，後端會 fallback 至已存的

      setTestingOdoo(true);
      const token = localStorage.getItem('token');
      if (!token) {
        message.error(t('common.loginRequired'));
        setTestingOdoo(false);
        return;
      }

      const body = {
        host: formValues.apiUrl,
        database: formValues.odooDatabase,
        login: formValues.odooDefaultLogin,
        apiKey,
        protocol: formValues.odooProtocol || 'xmlrpc',
        defaultCompanyId: formValues.odooDefaultCompanyId ?? null,
        timeoutSeconds: formValues.odooTimeoutSeconds || 30
      };

      const response = await fetch(`/api/apiproviders/test-odoo/${provider.providerKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (!response.ok || result.ok === false) {
        const detail = result?.error || result?.message || `HTTP ${response.status}`;
        const trace = Array.isArray(result?.trace) ? result.trace : [];
        const summaryLines = [
          `host=${result?.host || body.host || '-'}`,
          `database=${result?.database || body.database || '-'}`,
          `login=${result?.login || body.login || '-'}`,
          `protocol=${result?.protocol || body.protocol}`,
          `apiKey length=${result?.apiKeyLength ?? (apiKey ? apiKey.length : 0)} masked=${result?.apiKeyMasked || '(none)'}`,
          `apiKeyFromRequestBody=${result?.apiKeyFromRequestBody}`,
          `serverSerie=${result?.serverSerie || '-'}`,
          `uid=${result?.uid ?? '-'}`,
        ];

        Modal.error({
          title: t('apiProviders.form.testOdooFailed') || 'Odoo connection failed',
          width: 720,
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>
                <strong>{detail}</strong>
              </div>
              <pre style={{
                background: '#f6f8fa',
                padding: 8,
                fontSize: 12,
                lineHeight: 1.5,
                maxHeight: 240,
                overflow: 'auto',
                margin: 0,
                marginBottom: 8
              }}>
                {summaryLines.join('\n')}
              </pre>
              {trace.length > 0 && (
                <>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Trace:</div>
                  <pre style={{
                    background: '#fff7e6',
                    border: '1px solid #ffd591',
                    padding: 8,
                    fontSize: 12,
                    lineHeight: 1.5,
                    maxHeight: 320,
                    overflow: 'auto',
                    margin: 0
                  }}>
                    {trace.join('\n')}
                  </pre>
                </>
              )}
              {result?.stack && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', color: '#666', fontSize: 12 }}>Stack trace</summary>
                  <pre style={{ fontSize: 11, marginTop: 4, maxHeight: 200, overflow: 'auto' }}>{result.stack}</pre>
                </details>
              )}
            </div>
          )
        });
        return;
      }

      const ver = result.serverSerie || result.serverVersion || '';
      message.success(
        (t('apiProviders.form.testOdooSuccess') || 'Odoo connection OK')
          + ` (uid=${result.uid || '-'}, version=${ver}, protocol=${result.protocol || 'xmlrpc'})`
      );
    } catch (error) {
      if (error.errorFields) {
        message.error(t('apiProviders.form.testOdooValidationError') || 'Please fill in apiUrl / database / defaultLogin');
        return;
      }
      Modal.error({
        title: t('apiProviders.form.testOdooFailed') || 'Odoo connection failed',
        content: error.message || 'Unknown error'
      });
    } finally {
      setTestingOdoo(false);
    }
  };

  const handleTestEmailClick = async () => {
    try {
      // 先驗證基本配置欄位
      await form.validateFields([
        'tenantId',
        'clientId',
        'clientSecret',
        'fromEmail'
      ]);

      // 設置測試郵件表單的預設值
      const formValues = form.getFieldsValue();
      testEmailForm.setFieldsValue({
        toEmail: formValues.fromEmail, // 預設發給自己
        subject: t('apiProviders.form.testEmailSubject'),
        body: t('apiProviders.form.testEmailBody')
      });

      // 打開測試郵件彈窗
      setTestEmailModalOpen(true);
    } catch (error) {
      if (error.errorFields) {
        message.error(t('apiProviders.form.testEmailValidationError'));
      }
    }
  };

  const handleTestEmailSubmit = async () => {
    try {
      const testValues = await testEmailForm.validateFields();
      const formValues = form.getFieldsValue();

      setTestingEmail(true);
      setTestEmailModalOpen(false);

      const token = localStorage.getItem('token');
      if (!token) {
        message.error(t('common.loginRequired'));
        return;
      }

      // 構建測試請求
      const testRequest = {
        tenantId: formValues.tenantId,
        clientId: formValues.clientId,
        clientSecret: formValues.clientSecret,
        fromEmail: formValues.fromEmail,
        replyTo: formValues.replyTo || formValues.fromEmail,
        toEmail: testValues.toEmail,
        subject: testValues.subject,
        body: testValues.body
      };

      const response = await fetch(`/api/apiproviders/test-email/${provider.providerKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testRequest)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('apiProviders.form.testEmailFailed'));
      }

      message.success(t('apiProviders.form.testEmailSuccess'));
      testEmailForm.resetFields();
    } catch (error) {
      console.error('Test email failed:', error);
      if (error.errorFields) {
        // 表單驗證錯誤
        message.error(t('apiProviders.form.testEmailValidationError'));
        setTestEmailModalOpen(true); // 保持彈窗打開
      } else {
        message.error(error.message || t('apiProviders.form.testEmailFailed'));
      }
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okButtonProps={{ disabled: fetching || !provider }}
      title={provider ? t('apiProviders.modal.title', { name: provider.displayName }) : t('apiProviders.modal.loadingTitle')}
      okText={t('apiProviders.modal.save')}
      cancelText={t('apiProviders.modal.cancel')}
      width={640}
    >
      <Spin spinning={fetching} tip={t('common.loading')}>
        {provider && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            message={provider.displayName}
            description={
              <div>
                <Paragraph style={{ marginBottom: 4 }}>
                  {t(`apiProviders.description.${provider.providerKey}`, provider.description) || provider.description || t('apiProviders.noDescription')}
                </Paragraph>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('apiProviders.modal.providerKeyLabel', 'Provider Key')}: <Text code style={{ fontSize: 12 }}>{provider.providerKey}</Text>
                </Text>
              </div>
            }
            showIcon
          />

          <div className="api-provider-modal__defaults">
            <Paragraph>
              <Text strong>{t('apiProviders.modal.defaultUrl')}:</Text>
              <br />
              <Text code>{provider.defaultApiUrl}</Text>
            </Paragraph>
            {provider.defaultModel && (
              <Paragraph>
                <Text strong>{t('apiProviders.modal.defaultModel')}:</Text>
                <br />
                <Text code>{provider.defaultModel}</Text>
              </Paragraph>
            )}
          </div>

          <Divider />

          <Form
            layout="vertical"
            form={form}
            requiredMark={false}
          >
            <Form.Item name="active" label={t('apiProviders.form.active')} valuePropName="checked">
              <Switch checkedChildren={t('apiProviders.status.active')} unCheckedChildren={t('apiProviders.status.inactive')} />
            </Form.Item>

            <Form.Item 
              name="apiUrl" 
              label={t('apiProviders.form.apiUrl')} 
              rules={[{ required: true, message: t('apiProviders.form.apiUrlRequired') }]}
              tooltip={isEmailServerCategory ? t('apiProviders.form.apiUrlEmailServerTip') : undefined}
            > 
              <Input placeholder={provider.defaultApiUrl} />
            </Form.Item>

            <Form.Item name="authType" label={t('apiProviders.form.authType')} tooltip={t('apiProviders.form.authTypeTip')}>
              <Select
                options={authTypeOptions}
                placeholder={t('apiProviders.form.authTypePlaceholder')}
                allowClear
              />
            </Form.Item>

            {isAiCategory && (
              <>
                {supportedModels.length > 0 ? (
                  <Form.Item name="model" label={t('apiProviders.form.model')} rules={[{ required: true, message: t('apiProviders.form.modelRequired') }]}> 
                    <Select
                      options={supportedModels}
                      placeholder={provider.defaultModel || t('apiProviders.form.modelPlaceholder')}
                      allowClear
                      showSearch
                      filterOption={(input, option) => {
                        // 如果 option 有 searchText，使用它；否則嘗試從 label 提取文本
                        const searchText = option?.searchText || 
                          (typeof option?.label === 'string' ? option.label : option?.value || '');
                        return searchText.toLowerCase().includes(input.toLowerCase());
                      }}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item name="model" label={t('apiProviders.form.model')}>
                    <Input placeholder={provider.defaultModel || t('apiProviders.form.modelPlaceholder')} />
                  </Form.Item>
                )}

                <Form.Item name="temperature" label={t('apiProviders.form.temperature')} tooltip={t('apiProviders.form.temperatureTip')}>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={provider.temperatureMin ?? 0}
                    max={provider.temperatureMax ?? 2}
                    step={0.1}
                  />
                </Form.Item>

                <Form.Item name="topP" label={t('apiProviders.form.topP')} tooltip={t('apiProviders.form.topPTip')}>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={1}
                    step={0.05}
                  />
                </Form.Item>

                <Form.Item name="enableStreaming" label={t('apiProviders.form.enableStreaming')} valuePropName="checked">
                  <Switch disabled={!provider.definitionEnableStreaming} />
                </Form.Item>

                <Form.Item name="structuredOutputMode" hidden>
                  <Input />
                </Form.Item>

                <Form.Item name="structuredOutputStrict" hidden valuePropName="checked">
                  <Switch />
                </Form.Item>
              </>
            )}

            {/* MS Graph (EmailServer) 使用 OAuth 2.0，不需 API Key，僅非 EmailServer 時顯示 */}
            {!isEmailServerCategory && (
              <Form.Item label={t('apiProviders.form.apiKey')}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {provider.hasApiKey && (
                    <Text type="secondary">{t('apiProviders.form.maskedKey', { key: provider.maskedApiKey })}</Text>
                  )}
                  <Form.Item name="apiKey" noStyle>
                    <Input.Password
                      placeholder={t('apiProviders.form.apiKeyPlaceholder')}
                      visibilityToggle
                      allowClear
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      spellCheck={false}
                    />
                  </Form.Item>
                  {provider.hasApiKey && (
                    <Form.Item name="clearApiKey" valuePropName="checked" noStyle>
                      <Checkbox>{t('apiProviders.form.clearApiKey')}</Checkbox>
                    </Form.Item>
                  )}
                </Space>
              </Form.Item>
            )}

            {showManagementApiKey && (
              <Form.Item
                label={t('apiProviders.form.managementApiKey')}
                tooltip={t('apiProviders.form.managementApiKeyHint')}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {provider.hasManagementApiKey && (
                    <Text type="secondary">{t('apiProviders.form.maskedKey', { key: provider.maskedManagementApiKey })}</Text>
                  )}
                  <Form.Item name="managementApiKey" noStyle>
                    <Input.Password
                      placeholder={t('apiProviders.form.managementApiKeyPlaceholder')}
                      visibilityToggle
                      allowClear
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      spellCheck={false}
                    />
                  </Form.Item>
                  {provider.hasManagementApiKey && (
                    <Form.Item name="clearManagementApiKey" valuePropName="checked" noStyle>
                      <Checkbox>{t('apiProviders.form.clearManagementApiKey')}</Checkbox>
                    </Form.Item>
                  )}
                </Space>
              </Form.Item>
            )}

            <Form.Item label={t('apiProviders.form.extraHeaders')} tooltip={t('apiProviders.form.extraHeadersTip')}>
              <Form.Item name="extraHeadersJson" noStyle>
                <TextArea rows={3} placeholder='{ "X-Custom-Header": "value" }' allowClear />
              </Form.Item>
            </Form.Item>

            {isEmailServerCategory ? (
              <>
                <Divider orientation="left">{t('apiProviders.form.oauthConfig')}</Divider>
                <Form.Item 
                  name="tenantId" 
                  label={t('apiProviders.form.tenantId')}
                  rules={[{ required: true, message: t('apiProviders.form.tenantIdRequired') }]}
                  tooltip={t('apiProviders.form.tenantIdTip')}
                >
                  <Input placeholder="your-tenant-id" allowClear />
                </Form.Item>

                <Form.Item 
                  name="clientId" 
                  label={t('apiProviders.form.clientId')}
                  rules={[{ required: true, message: t('apiProviders.form.clientIdRequired') }]}
                  tooltip={t('apiProviders.form.clientIdTip')}
                >
                  <Input placeholder="your-client-id" allowClear />
                </Form.Item>

                <Form.Item 
                  name="clientSecret" 
                  label={t('apiProviders.form.clientSecret')}
                  rules={[{ required: true, message: t('apiProviders.form.clientSecretRequired') }]}
                  tooltip={t('apiProviders.form.clientSecretTip')}
                >
                  <Input.Password
                    placeholder="your-client-secret"
                    visibilityToggle
                    allowClear
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    spellCheck={false}
                  />
                </Form.Item>

                <Divider orientation="left">{t('apiProviders.form.emailSettings')}</Divider>
                {/* fromEmail：同時用於「發件」與 Start 節點 Email 監聽的收件信箱；語言包 key: apiProviders.form.fromEmail / fromEmailTip */}
                <Form.Item 
                  name="fromEmail" 
                  label={t('apiProviders.form.fromEmail')}
                  rules={[
                    { required: true, message: t('apiProviders.form.fromEmailRequired') },
                    { type: 'email', message: t('apiProviders.form.fromEmailInvalid') }
                  ]}
                  tooltip={t('apiProviders.form.fromEmailTip')}
                >
                  <Input placeholder="sender@example.com" allowClear />
                </Form.Item>

                <Form.Item 
                  name="replyTo" 
                  label={t('apiProviders.form.replyTo')}
                  rules={[{ type: 'email', message: t('apiProviders.form.replyToInvalid') }]}
                  tooltip={t('apiProviders.form.replyToTip')}
                >
                  <Input placeholder="reply@example.com" allowClear />
                </Form.Item>

                <Form.Item 
                  name="saveToSentItems" 
                  label={t('apiProviders.form.saveToSentItems')} 
                  valuePropName="checked"
                  tooltip={t('apiProviders.form.saveToSentItemsTip')}
                >
                  <Switch checkedChildren={t('apiProviders.form.enabled')} unCheckedChildren={t('apiProviders.form.disabled')} />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={handleTestEmailClick}
                    loading={testingEmail}
                    block
                  >
                    {t('apiProviders.form.testEmail')}
                  </Button>
                </Form.Item>
              </>
            ) : isErpCategory ? (
              <>
                <Divider orientation="left">{t('apiProviders.form.odooSettings') || 'Odoo Settings'}</Divider>
                <Form.Item
                  name="odooProtocol"
                  label={t('apiProviders.form.odooProtocol') || 'Protocol'}
                  tooltip={t('apiProviders.form.odooProtocolTip') || 'XML-RPC: 所有 Odoo 部署皆支援。JSON-2: 僅 Odoo Custom plan 支援；其他方案請選 XML-RPC。'}
                >
                  <Select
                    options={[
                      { value: 'xmlrpc', label: 'XML-RPC (recommended)' },
                      { value: 'json2', label: 'JSON-2 (Odoo 19, Custom plan only)' }
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  name="odooDatabase"
                  label={t('apiProviders.form.odooDatabase') || 'Database'}
                  rules={[{ required: true, message: t('apiProviders.form.odooDatabaseRequired') || 'Database is required' }]}
                  tooltip={t('apiProviders.form.odooDatabaseTip') || 'Odoo database name; same value you see on the login page'}
                >
                  <Input placeholder="my_company_db" allowClear />
                </Form.Item>

                <Form.Item
                  name="odooDefaultLogin"
                  label={t('apiProviders.form.odooDefaultLogin') || 'Service Login'}
                  rules={[{ required: true, message: t('apiProviders.form.odooDefaultLoginRequired') || 'Login is required' }]}
                  tooltip={t('apiProviders.form.odooDefaultLoginTip') || '建議使用專屬整合帳號，並在 Odoo Settings → Users → API Keys 產生 Key'}
                >
                  <Input placeholder="integration@your-company.com" allowClear />
                </Form.Item>

                <Form.Item
                  name="odooDefaultCompanyId"
                  label={t('apiProviders.form.odooDefaultCompanyId') || 'Default Company ID (optional)'}
                  tooltip={t('apiProviders.form.odooDefaultCompanyIdTip') || '多公司情境下會強制 Odoo context.allowed_company_ids；單公司可留空'}
                >
                  <InputNumber style={{ width: '100%' }} min={1} placeholder="1" />
                </Form.Item>

                <Form.Item
                  name="odooPort"
                  label={t('apiProviders.form.odooPort') || 'Port'}
                  tooltip={t('apiProviders.form.odooPortTip') || '預設 443 (HTTPS)；自架 / 內網可能是 8069'}
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={65535} />
                </Form.Item>

                <Form.Item
                  name="odooTimeoutSeconds"
                  label={t('apiProviders.form.odooTimeoutSeconds') || 'Timeout (seconds)'}
                >
                  <InputNumber style={{ width: '100%' }} min={5} max={600} />
                </Form.Item>

                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={t('apiProviders.form.odooApiKeyHint')
                    || 'API Key：在 Odoo Settings → Users → API Keys 產生（Odoo 14+）；JSON-2 內 Key 需每 3 個月輪換一次。'}
                />

                <Form.Item>
                  <Button
                    type="primary"
                    icon={<ApiOutlined />}
                    onClick={handleTestOdooClick}
                    loading={testingOdoo}
                    block
                  >
                    {t('apiProviders.form.testOdoo') || 'Test Odoo Connection'}
                  </Button>
                </Form.Item>

                <Divider orientation="left">{t('apiProviders.form.advanced') || 'Advanced'}</Divider>
                <Form.Item
                  label={t('apiProviders.form.settingsJson')}
                  tooltip={t('apiProviders.form.settingsJsonTip')}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Form.Item name="settingsJson" noStyle>
                      <TextArea rows={4} allowClear />
                    </Form.Item>
                    <Space size="small">
                      <Button size="small" onClick={handleResetSettings} disabled={!provider?.defaultSettingsJson}>
                        {t('apiProviders.form.resetSettings')}
                      </Button>
                      {provider?.defaultSettingsJson && (
                        <Text type="secondary">{t('apiProviders.form.resetSettingsHint')}</Text>
                      )}
                    </Space>
                  </Space>
                </Form.Item>
              </>
            ) : isEmbeddingCategory ? (
              <>
                <Divider orientation="left">{t('apiProviders.form.embeddingSettings')}</Divider>
                <Form.Item
                  name="model"
                  label={t('apiProviders.form.model')}
                  rules={[{ required: true, message: t('apiProviders.form.modelRequired') }]}
                  extra={
                    <Button
                      type="link"
                      size="small"
                      loading={listingEmbeddingModels}
                      icon={<SearchOutlined />}
                      onClick={handleListEmbeddingModelsClick}
                      style={{ padding: 0, height: 'auto', marginTop: 2 }}
                      disabled={!provider?.providerKey}
                    >
                      {t('apiProviders.form.queryAvailableModels') || '查詢此 Provider 可用模型'}
                    </Button>
                  }
                >
                  <Input placeholder={provider?.defaultModel || t('apiProviders.form.modelPlaceholder')} allowClear />
                </Form.Item>
                {provider?.providerKey === 'copilot-embeddings' && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={t('apiProviders.form.embeddingExperimentalHint')}
                  />
                )}
                <Button
                  type="default"
                  icon={<ApiOutlined />}
                  loading={testingEmbedding}
                  onClick={handleTestEmbeddingClick}
                  style={{ marginBottom: 12 }}
                >
                  {t('apiProviders.form.testEmbedding')}
                </Button>
                <Divider orientation="left">{t('apiProviders.form.advanced')}</Divider>
                <Form.Item
                  label={t('apiProviders.form.settingsJson')}
                  tooltip={t('apiProviders.form.settingsJsonTip')}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Form.Item name="settingsJson" noStyle>
                      <TextArea rows={4} allowClear />
                    </Form.Item>
                    <Space size="small">
                      <Button size="small" onClick={handleResetSettings} disabled={!provider?.defaultSettingsJson}>
                        {t('apiProviders.form.resetSettings')}
                      </Button>
                    </Space>
                  </Space>
                </Form.Item>
              </>
            ) : isVoiceCategory ? (
              <>
                <Divider orientation="left">{t('apiProviders.form.voiceSettings') || 'Speech-to-Text Settings'}</Divider>
                <Form.Item
                  name="voiceLanguage"
                  label={t('apiProviders.form.voiceLanguage') || 'Default Language Code'}
                  tooltip={t('apiProviders.form.voiceLanguageTip')}
                >
                  <Input placeholder="zh" allowClear maxLength={10} />
                </Form.Item>

                <Form.Item
                  name="voiceFormat"
                  label={t('apiProviders.form.voiceFormat') || 'Enable ITN Formatting (format)'}
                  valuePropName="checked"
                  tooltip={t('apiProviders.form.voiceFormatTip')}
                >
                  <Switch />
                </Form.Item>

                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={t('apiProviders.form.voiceApiKeyHint') || 'x.ai STT uses the same Bearer API key as the Chat API.'}
                />

                <Divider orientation="left">{t('apiProviders.form.advanced') || 'Advanced'}</Divider>
                <Form.Item
                  label={t('apiProviders.form.settingsJson')}
                  tooltip={t('apiProviders.form.settingsJsonTip')}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Form.Item name="settingsJson" noStyle>
                      <TextArea rows={4} allowClear />
                    </Form.Item>
                    <Space size="small">
                      <Button size="small" onClick={handleResetSettings} disabled={!provider?.defaultSettingsJson}>
                        {t('apiProviders.form.resetSettings')}
                      </Button>
                      {provider?.defaultSettingsJson && (
                        <Text type="secondary">{t('apiProviders.form.resetSettingsHint')}</Text>
                      )}
                    </Space>
                  </Space>
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item 
                  label={t('apiProviders.form.authConfigJson')} 
                  tooltip={t('apiProviders.form.authConfigTip')}
                >
                  <Form.Item name="authConfigJson" noStyle>
                    <TextArea 
                      rows={3} 
                      placeholder='{ "tenantId": "...", "clientId": "..." }' 
                      allowClear 
                    />
                  </Form.Item>
                </Form.Item>

                <Form.Item 
                  label={t('apiProviders.form.settingsJson')} 
                  tooltip={t('apiProviders.form.settingsJsonTip')}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Form.Item name="settingsJson" noStyle>
                      <TextArea 
                        rows={4} 
                        placeholder='{ "temperature": 0.8 }' 
                        allowClear 
                      />
                    </Form.Item>
                    <Space size="small">
                      <Button size="small" onClick={handleResetSettings} disabled={!provider?.defaultSettingsJson}>
                        {t('apiProviders.form.resetSettings')}
                      </Button>
                      {provider?.defaultSettingsJson && (
                        <Text type="secondary">{t('apiProviders.form.resetSettingsHint')}</Text>
                      )}
                    </Space>
                  </Space>
                </Form.Item>
              </>
            )}
          </Form>
          </Space>
        )}
      </Spin>

      {/* 測試郵件彈窗 */}
      <Modal
        open={testEmailModalOpen}
        onCancel={() => {
          setTestEmailModalOpen(false);
          testEmailForm.resetFields();
        }}
        onOk={handleTestEmailSubmit}
        confirmLoading={testingEmail}
        title={t('apiProviders.form.testEmailModalTitle')}
        okText={t('apiProviders.form.sendTestEmail')}
        cancelText={t('apiProviders.modal.cancel')}
        width={600}
      >
        <Form
          form={testEmailForm}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="toEmail"
            label={t('apiProviders.form.testEmailTo')}
            rules={[
              { required: true, message: t('apiProviders.form.testEmailToRequired') },
              { type: 'email', message: t('apiProviders.form.testEmailToInvalid') }
            ]}
            tooltip={t('apiProviders.form.testEmailToTip')}
          >
            <Input placeholder="recipient@example.com" allowClear />
          </Form.Item>

          <Form.Item
            name="subject"
            label={t('apiProviders.form.testEmailSubjectLabel')}
            rules={[{ required: true, message: t('apiProviders.form.testEmailSubjectRequired') }]}
          >
            <Input placeholder={t('apiProviders.form.testEmailSubject')} allowClear />
          </Form.Item>

          <Form.Item
            name="body"
            label={t('apiProviders.form.testEmailBodyLabel')}
            rules={[{ required: true, message: t('apiProviders.form.testEmailBodyRequired') }]}
          >
            <TextArea
              rows={6}
              placeholder={t('apiProviders.form.testEmailBody')}
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default ApiProviderSettingsModal;

